import { useState, useEffect } from 'react';
import { DocumentVersionORM, CustomDeckORM, Presentable } from 'src/types/types';
import { fetchPagesJson } from 'src/utils/loadCloudfrontAsset/common';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import logger from 'src/utils/logger';
import { getCurrentDocVer } from 'src/state/machines/sharing/shareUtils';

export interface Recommendations {
  pageId: string
  score: number
}

// [TODO] - Extract this out into a higher, more visible level
export interface ContentPageData {
  pageId: string;
  content?: string;
  presentationPageNumber: number;
  enableSlideTextInsertion?: boolean;
  speakerNotes?: string | null;
  s3Key: string;
  title?: string;
  recommendations: Recommendations[];
  mapping: string | null;
  originalTitle?: string;
  originalSpeakerNotes?: string;
}

// External cache to store fetched data
const cache: { [key: string]: ContentPageData[] } = {};
/**
 * Function for fetching the JSON file contents of a given presentable
 * @param presentable: Presentable
 * @param disableCache: boolean - Disables cache if true
 * @param showOriginalData: boolean - if true, originalTitle & originalSpeakerNotes will be in the return value if there is any.
 * @returns { matches: MatchedPage[], totalMatchCount: number }
 */
export const useContentPageData = (presentable?: Presentable, disableCache?: boolean, showOriginalData?: boolean) => {
  const [contentPageData, setContentPageData] = useState<ContentPageData[] | undefined>(undefined);
  logger.contentPresenting.contentPageData.debug(
    '[START] Inside useContentPageData',
    { cacheKey: presentable?.orm.model.id },
  )

  // Fetches
  useEffect(() => {
    if (!presentable) return;

    // Create an abort controller to cancel async tasks if the component unmounts
    const controller = new AbortController();
    const { signal } = controller;
    // Check if data is already cached
    const cacheKey = presentable.orm.model.id;
    logger.contentPresenting.contentPageData.debug('[UPDATE] inside useEffect', { cacheKey })

    if (cacheKey in cache && !disableCache) {
      logger.contentPresenting.contentPageData.debug('[END] Using cached data', { cacheKey });
      setContentPageData(cache[cacheKey]);
      return;
    }

    // Measure fetch time
    const fetchStartTime = performance?.now() ?? Date.now();
    logger.contentPresenting.contentPageData.debug('Start fetching page data...', { cacheKey })

    // Fetch the data and update the cache
    // [TODO] - Decouple this logic
    const getDocumentsTextsAndFormat = async () => {
      const s3Keys = presentable.presentablePages.reduce<{ [key: string]: string }>((acc, page) => {
        // since we are storing the titles and speaker notes in the json file, we need to make sure we are
        // getting the correct version of the file. If the file has been updated in the last 10 minutes,
        // we will use the current version of the file. Otherwise, we will use the version of the file
        // that was published at the time of the presentation. This is to ensure that the titles and
        // speaker notes are correct for the presentation, and avoid any caching issues (disk and cloudfront).
        const documentVersion = page.documentVersionORM

        if (!documentVersion) return acc
        if (documentVersion.model.conversionStatus !== 'PROCESSED') {
          // eslint-disable-next-line max-len
          logger.contentPresenting.contentPageData.verbose('documentVersion is not processed', { documentVersionId: documentVersion.model.id })
          return acc
        }

        if (!documentVersion.model.convertedFolderKey) return acc
        if (!page.documentVersionORM.model.convertedFolderKey) return acc
        const param = `?srcUpdatedAt=${page.documentVersionORM.model.updatedAt}`;
        acc[page.documentVersionORM.model.id] =
          `${page.documentVersionORM.model.convertedFolderKey}json/Pages.json${param}`;
        return acc;
      }, {});

      try {
        // Check if the abort signal is already triggered before starting
        if (signal.aborted) {
          setContentPageData([]);
          return;
        }

        const responses = await fetchPagesJson(s3Keys, disableCache);

        if (!responses || signal.aborted) {
          setContentPageData([]);
          return;
        }

        const pages: ContentPageData[] = [];
        // ASSIGNS THE CONTENT TO EACH PAGE
        presentable.presentablePages.forEach((page) => {
          const formattedPage: ContentPageData = {
            pageId: page.documentVersionORM.model.id + '_' + page.page.number,
            presentationPageNumber: page.presentationPageNumber,
            s3Key: detectArchivedFileKeyPath(page.documentVersionORM.model, page.page, 'sm') || '',
            recommendations: [],
            mapping: null,
          };

          // FINDS THE CONTENT FOR THE PAGE AND ASSIGNS IT
          responses.forEach((response) => {
            if (response.documentVersionId === page.documentVersionORM.model.id) {
              // FINDS THE PAGE
              response.content.pages.forEach((responsePage) => {
                if (responsePage?.number === page.page.number) {
                  formattedPage.speakerNotes = responsePage.overrideSpeakerNotes ?? responsePage.speakerNotes;
                  formattedPage.content = responsePage.content;
                  formattedPage.title = responsePage.overrideTitle ?? responsePage.title;
                  formattedPage.recommendations = responsePage.recommendations ?? [];
                  formattedPage.enableSlideTextInsertion = !!responsePage.enableSlideTextInsertion;
                  formattedPage.mapping = responsePage.mapping ?? null;
                  formattedPage.originalTitle = showOriginalData ? responsePage.title : undefined;
                  formattedPage.originalSpeakerNotes = showOriginalData
                    ? responsePage.speakerNotes || undefined
                    : undefined;
                }
              });
            }
          });
          pages.push(formattedPage);
        });

        // Update cache and state
        cache[cacheKey] = pages;
        setContentPageData(pages);
      } catch (error) {
        if (!signal.aborted) {
          // Only log error if not aborted
          const errorText = 'Error fetching pages data';
          logger.contentPresenting.contentPageData.warn(errorText, { cacheKey, error });
        }
      }

      // Measure fetch time
      const fetchEndTime = performance.now();
      logger.contentPresenting.contentPageData.debug(
        `[END] Finished fetching page data. Fetch time: ${(fetchEndTime - fetchStartTime).toFixed(2)} ms`,
        { cacheKey },
      )
    };

    setContentPageData(undefined);
    getDocumentsTextsAndFormat();

    // Cleanup function to abort fetch on unmount
    return () => {
      controller.abort();
    };
  }, [
    presentable?.orm.model.id,
    // eslint-disable-next-line
    presentable?.orm.model['conversionStatus'], // this // eslint-disable-next-line is required due when we  try to fetch the data from the pages.json we need to guarantee that the document version is processed, if not, we will not be able to fetch the data from the pages.json processed and we will get empty recommendations
    presentable?.numberOfPages,
    disableCache,
  ]);

  if (presentable && cache[presentable.orm.model.id] && !disableCache) {
    logger.contentPresenting.contentPageData.debug('[END] Using cached data', { cacheKey: presentable.orm.model.id });
    return {
      contentPageData: cache[presentable.orm.model.id],
    };
  }

  return {
    contentPageData: contentPageData || [],
  }
}

// [TODO] - A lot of these can probably be exposed at the ORM level
export const getSlideContentPageDataTitle = (
  idx: number,
  contentPageData?: ContentPageData[],
  showDefaultTitle: boolean = false,
): string => {
  const defaultTitle = showDefaultTitle ? `Slide ${idx + 1}` : '';
  if (!contentPageData || !contentPageData[idx]) return defaultTitle
  return contentPageData[idx].title || defaultTitle
}

export const hasSpeakerNotes = (idx: number, contentPageData?: ContentPageData[]): boolean => {
  if (!contentPageData || !contentPageData[idx]) return false
  return !!contentPageData[idx].speakerNotes
}

export const hasSlideTextInsertionEnabled = (idx: number, contentPageData?: ContentPageData[]): boolean => {
  if (!contentPageData || !contentPageData[idx]) return false
  return !!contentPageData[idx].enableSlideTextInsertion
}

// [TODO] - Expose this as part of the ORM object?
/**
 * this function generates all s3Keys of each document version within a custom deck
 * @param customDeckORM the custom deck to get all the s3Keys of all the document versions
 * @param latest boolean, if ture, return the latest version
 * @returns and object where each key is the docVerId from slides within custom deck and the value is the s3Key of that documentVersion
 */
export const getS3KeysWithinCustomDeck = (
  customDeckORM: CustomDeckORM,
  latest?: boolean,
): { [documentVersionId: string]: string } => {
  const s3Keys = customDeckORM.model.groups.reduce<{ [key: string]: string }>((acc, group) => {
    const documentVersionORM = getCurrentDocVer(group.pages[0].documentVersionId)

    const docVerORM = latest
      ? documentVersionORM?.relations.documentORM.relations.version.latestPublishedDocumentVersionORM
      : documentVersionORM

    if (!docVerORM) {
      return acc
    }

    if (docVerORM.model.convertedFolderKey && !acc[docVerORM.model.id]) {
      acc[docVerORM.model.id!] =
        `${docVerORM?.model.convertedFolderKey}json/Pages.json?srcUpdatedAt=${docVerORM.model.updatedAt}`
    }
    return acc
  }, {})

  return s3Keys
}

// [TODO] - Expose this as part of the ORM object?
// 3. Add the timestamp (10 minute for optimization) check to help avoid caching issues
// 4. (FUTURE) - Consider guaranteeing pages.json is fresh by
//               Creating a copy in a different directory that is actually frozen
export const getPageJSONS3KeyForDocVer = (docVerORM: DocumentVersionORM): string | undefined => {
  if (docVerORM.model.convertedFolderKey) {
    return `${docVerORM.model.convertedFolderKey}json/Pages.json?srcUpdatedAt=${docVerORM.model.updatedAt}`
  }
}

export default useContentPageData;
